import { useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { Icon } from "../lib/Icon";
import { usePortal } from "./state";
import { findProject, projectThread, sendProjectMessage, type PortalProject as Project } from "./projects";
import { BurnDown, SectionHeader } from "./components";

const MSTONE: Record<string, { bg: string; fg: string }> = {
  Done: { bg: "#e8f4ee", fg: "#1f8a5b" },
  "In progress": { bg: "#e8f0f3", fg: "#2a6f8a" },
  "At risk": { bg: "#fbf2dc", fg: "#9a6a12" },
  Scheduled: { bg: "#f0efe9", fg: "#6b706a" },
};
const TASK: Record<string, { bg: string; fg: string; label: string }> = {
  overdue: { bg: "#fbe9e7", fg: "#b3261e", label: "Overdue" },
  doing: { bg: "#e8f0f3", fg: "#2a6f8a", label: "In progress" },
  todo: { bg: "#f0efe9", fg: "#6b706a", label: "To do" },
  done: { bg: "#e8f4ee", fg: "#1f8a5b", label: "Done" },
};

// The instant reply reads ONLY the project record (DECISIONS.md §7) — hours,
// tasks, phase, milestones — and explicitly cannot agree scope, hours or dates.
function instantReply(p: Project): string {
  const remaining = Math.max(0, p.approvedHrs - p.loggedHrs);
  const next = p.milestones.find((m) => m[2] !== "Done");
  const overdue = p.myTasks.filter((t) => t[2] === "overdue").length;
  const parts = [
    `Thanks — this is on the ${p.name} thread and your project manager, ${p.pm}, will reply in person.`,
    `Right now the project is in "${p.phase}", ${p.pct}% complete against a ${p.plannedPct}% plan, with ${p.loggedHrs} of ${p.approvedHrs} approved hours logged (${remaining} remaining).`,
    next ? `The next milestone is "${next[0]}" (${next[1]}, ${String(next[2]).toLowerCase()}).` : "",
    overdue ? `You have ${overdue} task${overdue === 1 ? "" : "s"} marked overdue on your side.` : "",
    `I can only report what's in the project record — I can't agree scope, hours, or dates. ${p.pm} will confirm anything that changes those.`,
  ];
  return parts.filter(Boolean).join(" ");
}

export function PortalProject() {
  const { brand, brandId, user } = usePortal();
  const { id } = useParams({ strict: false }) as { id: string };
  const p = findProject(id, brandId);
  const [msg, setMsg] = useState("");
  const [, bump] = useState(0); // re-render after a message is written to the store
  // Read from the module store so a sent message survives leave/reopen.
  const thread = projectThread(id);

  if (!p) {
    return (
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "60px 28px", textAlign: "center" }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: "#1d241f" }}>Project not found</div>
        <p style={{ fontSize: 13.5, color: "#8a8e88", marginTop: 8 }}>You may not be a member of it, or it may have moved.</p>
        <Link to="/portal/projects" style={{ color: brand.ink, fontSize: 13.5 }}>Back to projects</Link>
      </div>
    );
  }

  function send(e: React.FormEvent) {
    e.preventDefault();
    const body = msg.trim();
    if (!body || !p) return;
    sendProjectMessage(p.id, body, instantReply(p));
    setMsg("");
    bump((n) => n + 1);
  }

  const remaining = Math.max(0, p.approvedHrs - p.loggedHrs);

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: "26px 28px 64px", display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "#8a8e88" }}>
        <Link to="/portal/projects" style={{ color: "#8a8e88", textDecoration: "none" }}>Projects</Link>
        <Icon name="chevron-right" size={13} color="#bcbfba" />
        <span style={{ fontFamily: "'Space Mono', monospace" }}>{p.id}</span>
      </div>

      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.6px", color: "#1d241f" }}>{p.name}</h1>
          <span style={{ fontSize: 11, fontWeight: 600, borderRadius: 2, padding: "2px 8px", background: p.health === "bad" ? "#fbe9e7" : p.health === "warn" ? "#fbf2dc" : "#e8f4ee", color: p.health === "bad" ? "#b3261e" : p.health === "warn" ? "#9a6a12" : "#1f8a5b" }}>{p.status}</span>
        </div>
        <div style={{ display: "flex", gap: 16, marginTop: 6, fontSize: 12.5, color: "#8a8e88", flexWrap: "wrap" }}>
          <span>{p.phase}</span><span>PM · {p.pm}</span><span>{p.start} → {p.go}</span>
        </div>
      </div>

      {p.health !== "ok" && p.concern && (
        <div style={{ display: "flex", gap: 11, alignItems: "flex-start", background: "#fdf6ec", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(191,124,21,.3)", padding: "13px 15px" }}>
          <Icon name="alert-triangle" size={16} color="#a06508" />
          <div style={{ fontSize: 13, lineHeight: 1.55, color: "#8a6a2f", textWrap: "pretty" }}>{p.concern}</div>
        </div>
      )}

      {/* Burn-down */}
      <section style={{ background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "16px 18px" }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 12 }}>
          <SectionHeader>Hours burn-down</SectionHeader>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 12.5, color: "#8a8e88" }}><b style={{ color: "#1d241f", fontFamily: "'Space Mono', monospace" }}>{p.loggedHrs}</b> logged</span>
          <span style={{ fontSize: 12.5, color: "#8a8e88" }}><b style={{ color: "#1d241f", fontFamily: "'Space Mono', monospace" }}>{remaining}</b> remaining</span>
          <span style={{ fontSize: 12.5, color: "#8a8e88" }}><b style={{ color: "#1d241f", fontFamily: "'Space Mono', monospace" }}>{p.approvedHrs}</b> approved</span>
        </div>
        <BurnDown labels={p.burn.labels} ideal={p.burn.ideal} actual={p.burn.actual} approved={p.approvedHrs} brand={brand} />
      </section>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))", gap: 16, alignItems: "start" }}>
        {/* Milestones */}
        <section style={{ background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "16px 18px" }}>
          <div style={{ marginBottom: 12 }}><SectionHeader>Milestones</SectionHeader></div>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            {p.milestones.map(([name, date, status, total, done]) => {
              const t = MSTONE[status] ?? MSTONE.Scheduled!;
              return (
                <div key={name} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 500, color: "#1d241f" }}>{name}</div>
                    <div style={{ fontSize: 11.5, color: "#8a8e88" }}>{date} · {done}/{total} tasks</div>
                  </div>
                  <span style={{ fontSize: 10.5, fontWeight: 600, borderRadius: 2, padding: "2px 8px", background: t.bg, color: t.fg, whiteSpace: "nowrap" }}>{status}</span>
                </div>
              );
            })}
          </div>
        </section>

        {/* My tasks */}
        <section style={{ background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "16px 18px" }}>
          <div style={{ marginBottom: 12 }}><SectionHeader>Your tasks</SectionHeader></div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {p.myTasks.map(([title, due, status, phase, note]) => {
              const t = TASK[status] ?? TASK.todo!;
              return (
                <div key={title}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 13.5, fontWeight: 500, color: "#1d241f", flex: 1, minWidth: 0 }}>{title}</span>
                    <span style={{ fontSize: 10.5, fontWeight: 600, borderRadius: 2, padding: "2px 7px", background: t.bg, color: t.fg }}>{t.label}</span>
                  </div>
                  <div style={{ fontSize: 11.5, color: "#8a8e88", marginTop: 2 }}>Due {due} · {phase}</div>
                  {note && <div style={{ fontSize: 12, color: "#8a6a2f", marginTop: 3, textWrap: "pretty" }}>{note}</div>}
                </div>
              );
            })}
            {p.myTasks.length === 0 && <div style={{ fontSize: 12.5, color: "#bcbfba" }}>Nothing assigned to you.</div>}
          </div>
        </section>
      </div>

      {/* Time audit — client-visible entries only */}
      <section style={{ background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "16px 18px" }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
          <SectionHeader>Logged time</SectionHeader>
          <span style={{ flex: 1 }} />
          {p.internalHidden > 0 && <span style={{ fontSize: 11.5, color: "#8a8e88" }}>{p.internalHidden} internal entries not shown</span>}
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          {p.timeLog.map(([date, who, , taskName, hours, note], i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderTop: i ? "1px solid #f4f3ee" : "none", fontSize: 12.5 }}>
              <span style={{ color: "#8a8e88", minWidth: 54 }}>{date}</span>
              <span style={{ fontWeight: 600, minWidth: 100 }}>{who}</span>
              <span style={{ flex: 1, minWidth: 0, color: "#4a504a" }}>{taskName}{note ? ` — ${note}` : ""}</span>
              <span style={{ fontFamily: "'Space Mono', monospace", color: "#1d241f" }}>{hours}h</span>
            </div>
          ))}
        </div>
      </section>

      {/* Team */}
      <section style={{ background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "16px 18px" }}>
        <div style={{ marginBottom: 12 }}><SectionHeader>Project team</SectionHeader></div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 220px), 1fr))", gap: 10 }}>
          {p.members.map(([name, role, side]) => {
            const you = name === user.name;
            const mine = side === "tribus";
            return (
              <div key={name} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ width: 30, height: 30, borderRadius: 3, background: mine ? brand.soft : "#f0efe9", color: mine ? brand.ink : "#4a504a", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flex: "none" }}>
                  {name.split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#1d241f" }}>{name}{you && <span style={{ fontSize: 10.5, fontWeight: 600, color: brand.ink, marginLeft: 6 }}>You</span>}</div>
                  <div style={{ fontSize: 11.5, color: "#8a8e88" }}>{role}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Message the team */}
      <section style={{ background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "16px 18px" }}>
        <div style={{ marginBottom: 10 }}><SectionHeader>Message the project team</SectionHeader></div>
        {thread.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
            {thread.map((m, i) => (
              <div key={i} style={{ display: "flex", flexDirection: m.from === "you" ? "row-reverse" : "row", gap: 10 }}>
                <span style={{ width: 26, height: 26, borderRadius: 3, flex: "none", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, background: m.from === "you" ? "#f0efe9" : brand.soft, color: m.from === "you" ? "#4a504a" : brand.ink }}>{m.from === "you" ? user.initials : "AI"}</span>
                <div style={{ maxWidth: "80%", background: m.from === "you" ? "#fff" : brand.soft, borderRadius: 3, boxShadow: m.from === "you" ? "inset 0 0 0 1px rgba(0,0,0,.08)" : `inset 0 0 0 1px ${brand.line}`, padding: "10px 13px", fontSize: 13, lineHeight: 1.55, color: "#2d332e", textWrap: "pretty" }}>{m.body}</div>
              </div>
            ))}
          </div>
        )}
        <form onSubmit={send}>
          <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} placeholder={`Message ${p.pm} and the ${p.name} team…`} style={{ width: "100%", border: "1px solid #dcdad2", borderRadius: 3, padding: "10px 12px", fontSize: 14, lineHeight: 1.6, color: "#1d241f", outline: 0, resize: "vertical", fontFamily: "inherit" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
            <span style={{ fontSize: 11.5, color: "#8a8e88", flex: 1 }}>Goes to the team on this project's thread — a person replies. The instant note only reads the project record.</span>
            <button type="submit" disabled={!msg.trim()} style={{ border: 0, background: msg.trim() ? brand.ink : "#f0efe9", color: msg.trim() ? "#fff" : "#8a8e88", borderRadius: 3, padding: "10px 18px", fontSize: 12, fontWeight: 600, letterSpacing: 1.2, textTransform: "uppercase", cursor: msg.trim() ? "pointer" : "not-allowed" }}>Send</button>
          </div>
        </form>
      </section>
    </div>
  );
}
